import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  X, Send, Sparkles, Search, Package, ListChecks, FileText, ArrowUpRight,
  Lightbulb, Zap, Factory, Boxes, Wrench, Cog,
} from "lucide-react";
import { API } from "../config/env";
import { useAuth } from "../context/AuthContext";

const SESSION_KEY = "debsun-ai-session";

const PROMPT_CARDS = [
  { icon: Cog, label: "Industrial Automation", prompt: "We're looking to automate part of our production line — PLC/SCADA integration, control panels and turnkey execution. Can you help us scope this?" },
  { icon: Lightbulb, label: "Industrial Lighting", prompt: "We need to plan industrial LED lighting for our facility — highbays, floodlights and control-grade fixtures. Where should we start?" },
  { icon: Zap, label: "Electrical Panels", prompt: "We need switchgear and control panel design for our plant. What information do you need from us to begin?" },
  { icon: Factory, label: "Factory Intelligence", prompt: "We want to monitor equipment and production data across our factory floor with an IIoT / factory intelligence layer. What would DEBSUN recommend?" },
  { icon: Boxes, label: "Digital Twins", prompt: "We're interested in a digital twin of one of our production lines for simulation and predictive insight. Is this something DEBSUN can help design?" },
  { icon: Wrench, label: "Custom Engineering", prompt: "We have an engineering challenge that doesn't fit a standard category. Here's what we're trying to solve:" },
];

const SECTION_DEFS = [
  { key: "Understanding", icon: Search },
  { key: "Recommended Solution", icon: Sparkles },
  { key: "Relevant Products", icon: Package },
  { key: "Engineering Services", icon: Cog },
  { key: "Next Steps", icon: ListChecks },
  { key: "Request Quote", icon: FileText },
];

const INDUSTRY_KEYWORDS = ["Manufacturing", "Steel", "Utilities", "Process Industries", "Renewable Energy", "Industrial Infrastructure"];

/** Builds a Client Portal quote-form prefill from a parsed AI response so a
 * "Request Quote" CTA can hand off the conversation context in one click. */
function buildQuotePrefill(parts) {
  const byLabel = {};
  parts.forEach((p) => { if (p.label) byLabel[p.label] = p.body; });
  const serviceSummary = [byLabel["Recommended Solution"], byLabel["Engineering Services"]].filter(Boolean).join(" ");
  const combined = `${byLabel["Understanding"] || ""} ${serviceSummary}`.toLowerCase();
  const industry = INDUSTRY_KEYWORDS.find((k) => combined.includes(k.toLowerCase())) || "";
  return {
    project_name: "Engineering Assistant Consultation",
    industry,
    notes: byLabel["Request Quote"] || "",
    items: [{ description: serviceSummary || byLabel["Understanding"] || "", quantity: 1 }],
  };
}

/** Presentation-only parser — looks for known section headers in the streamed
 * assistant text and splits it into labelled blocks. Falls back to plain text
 * when the model hasn't used the structure yet (e.g. the welcome message). */
function parseSections(text) {
  if (!text) return null;
  const labels = SECTION_DEFS.map((s) => s.key);
  const re = new RegExp(`(?:^|\\n)\\s*(?:#{1,3}\\s*|\\*\\*)?(${labels.join("|")})\\**\\s*:?\\s*`, "gi");
  const matches = [...text.matchAll(re)];
  if (matches.length === 0) return null;

  const parts = [];
  if (matches[0].index > 0) {
    const pre = text.slice(0, matches[0].index).trim();
    if (pre) parts.push({ label: null, body: pre });
  }
  matches.forEach((m, i) => {
    const label = labels.find((l) => l.toLowerCase() === m[1].toLowerCase());
    const bodyStart = m.index + m[0].length;
    const bodyEnd = i + 1 < matches.length ? matches[i + 1].index : text.length;
    parts.push({ label, body: text.slice(bodyStart, bodyEnd).trim() });
  });
  return parts;
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1.5 px-1 py-2" data-testid="ai-typing-indicator">
      {[0, 1, 2].map((i) => (
        <motion.span key={i} className="w-1.5 h-1.5 rounded-full bg-[color:var(--gold)]/70"
          animate={{ opacity: [0.25, 1, 0.25] }}
          transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.2, ease: "easeInOut" }} />
      ))}
    </div>
  );
}

function AssistantBubble({ text, streaming, onRequestQuote }) {
  const parts = useMemo(() => parseSections(text), [text]);

  if (!text) return streaming ? <TypingIndicator /> : null;

  if (!parts) {
    return (
      <div className="max-w-[88%] p-4 clip-frame-sm text-sm leading-relaxed whitespace-pre-wrap bg-white/[0.04] border border-white/10 text-[color:var(--console-text)]">
        {text}
      </div>
    );
  }

  return (
    <div className="max-w-[92%] space-y-3">
      {parts.map((p, i) => {
        if (!p.label) {
          return <p key={i} className="text-sm leading-relaxed text-[color:var(--console-text)] whitespace-pre-wrap">{p.body}</p>;
        }
        const def = SECTION_DEFS.find((s) => s.key === p.label);
        const Icon = def?.icon || Sparkles;
        const isQuote = p.label === "Request Quote";
        return (
          <div key={i}
            className={`p-4 clip-frame-sm border ${isQuote ? "border-[color:var(--gold)]/50 bg-[color:var(--gold)]/[0.07]" : "border-white/10 bg-white/[0.04]"}`}
            data-testid={`ai-section-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-2 mb-2">
              <Icon size={13} className={isQuote ? "text-[color:var(--gold)]" : "text-[color:var(--teal)]"} />
              <span className={`font-mono-2 text-[10px] tracking-[0.25em] uppercase ${isQuote ? "text-[color:var(--gold)]" : "text-[color:var(--console-text)]/60"}`}>
                {p.label}
              </span>
            </div>
            <p className="text-sm leading-relaxed text-[color:var(--console-text)] whitespace-pre-wrap">{p.body}</p>
            {isQuote && (
              <button onClick={() => onRequestQuote(buildQuotePrefill(parts))} data-testid="ai-request-quote-btn"
                className="mt-3 inline-flex items-center gap-2 btn-gold clip-frame-sm px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] font-mono-2">
                Start Quote Request <ArrowUpRight size={13} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

const STREAM_TIMEOUT_MS = 90000;

/** Extract SSE data payload from one event block (may contain multiple data: lines). */
function sseDataPayload(rawEvent) {
  const lines = [];
  for (const line of rawEvent.split("\n")) {
    if (line.startsWith("data:")) lines.push(line.slice(5).replace(/^ /, ""));
  }
  return lines.length ? lines.join("\n") : null;
}

export default function AISolutionFinder({ open, onClose }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Welcome to the DEBSUN Engineering Assistant.\n\nDescribe your project, technical challenge or operational requirement. I'll recommend suitable engineering services, products and digital solutions tailored to your application.",
    },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionIdRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // Sync lock — React state alone cannot prevent overlapping sends (stale closure).
  const inFlightRef = useRef(false);
  const abortRef = useRef(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) { sid = `dsn-${Math.random().toString(36).slice(2)}-${Date.now()}`; localStorage.setItem(SESSION_KEY, sid); }
    sessionIdRef.current = sid;
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus(), 400);
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); };
  }, [open, onClose]);

  // Abort any in-flight stream on unmount so loading cannot stick forever.
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  const send = async (text) => {
    const msg = text ?? input;
    if (!msg.trim() || inFlightRef.current) return;

    inFlightRef.current = true;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), STREAM_TIMEOUT_MS);

    // Pin assistant slot before setState so late chunks never write to a newer turn.
    const assistantIdx = messagesRef.current.length + 1;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setStreaming(true);

    const patchAssistant = (updater) => {
      setMessages((m) => {
        const idx = assistantIdx < m.length ? assistantIdx : m.length - 1;
        if (idx < 0 || !m[idx] || m[idx].role !== "assistant") return m;
        const c = [...m];
        const prev = c[idx].text || "";
        c[idx] = { role: "assistant", text: typeof updater === "function" ? updater(prev) : updater };
        return c;
      });
    };

    const consumeSSE = (rawEvent) => {
      const data = sseDataPayload(rawEvent);
      if (data == null) return false;
      if (data === "[DONE]") return true; // end of assistant turn
      if (data.startsWith("[ERROR]")) {
        console.warn("[AISolutionFinder] stream error payload", data);
        patchAssistant("Sorry — the assistant hit an error. Please retry.");
        return true;
      }
      patchAssistant((prev) => prev + data);
      return false;
    };

    try {
      const res = await fetch(`${API}/ai/solution-finder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionIdRef.current, message: msg }),
        signal: controller.signal,
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      if (!res.body) throw new Error("No stream");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finished = false;

      while (!finished) {
        const { done, value } = await reader.read();
        if (value) buffer += decoder.decode(value, { stream: true });
        if (done) {
          buffer += decoder.decode(); // flush decoder
        }

        const parts = buffer.split("\n\n");
        if (done) {
          // Flush leftover bytes that never got a trailing blank line.
          const rest = parts.pop() || "";
          if (rest.trim()) parts.push(rest);
          buffer = "";
        } else {
          buffer = parts.pop() || "";
        }

        for (const part of parts) {
          if (!part.trim()) continue;
          if (consumeSSE(part)) {
            finished = true;
            // Stop waiting on the socket — backend may still be persisting to DB
            // after [DONE], which previously left streaming=true forever.
            try { await reader.cancel(); } catch { /* ignore */ }
            break;
          }
        }

        if (done) break;
      }

      if (!finished && buffer.trim()) consumeSSE(buffer);
      // DONE with zero tokens previously left an empty bubble that looked like a hang.
      patchAssistant((prev) => prev || "No response received — please try again.");
    } catch (e) {
      if (e?.name === "AbortError") {
        console.warn("[AISolutionFinder] request aborted/timed out");
        patchAssistant((prev) => prev || "Request timed out — please try again.");
      } else {
        console.warn("[AISolutionFinder] request failed", e);
        patchAssistant((prev) => prev || "Network error — please try again.");
      }
    } finally {
      clearTimeout(timeoutId);
      if (abortRef.current === controller) abortRef.current = null;
      inFlightRef.current = false;
      setStreaming(false);
    }
  };

  const pickCard = (card) => {
    setInput(card.prompt);
    inputRef.current?.focus();
  };

  const handleRequestQuote = (prefill) => {
    sessionStorage.setItem("debsun-quote-prefill", JSON.stringify(prefill));
    sessionStorage.setItem("debsun-open-quote", "1");
    onClose();
    navigate(isAuthenticated ? "/portal" : "/login");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-md z-[70]"
            data-testid="ai-finder-backdrop"
          />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            role="dialog" aria-modal="true" aria-labelledby="ai-finder-title"
            className="fixed inset-y-0 right-0 z-[80] w-full sm:w-[540px] flex flex-col dsn-glass-panel"
            data-testid="ai-finder-panel"
          >
            {/* Header */}
            <div className="p-6 flex items-center justify-between relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-sm flex items-center justify-center" style={{ background: "var(--gold)", color: "#0A0E1A" }}>
                  <Sparkles size={16} />
                </div>
                <div>
                  <div id="ai-finder-title" className="display text-lg leading-none tracking-tight text-[color:var(--paper)]">
                    Engineering Assistant
                  </div>
                  <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--console-text)]/55 mt-1">
                    Industrial Solutions Advisor
                  </div>
                </div>
              </div>
              <button onClick={onClose} data-testid="ai-close-btn" aria-label="Close Engineering Assistant"
                className="w-9 h-9 border border-white/15 rounded-sm flex items-center justify-center text-[color:var(--console-text)]/70 hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]/60 transition-colors duration-400">
                <X size={16} />
              </button>
            </div>

            {/* Streaming pulse rail */}
            <div className="relative h-px bg-white/10 overflow-hidden">
              {streaming && (
                <motion.div
                  className="absolute inset-y-0 w-24 bg-gradient-to-r from-transparent via-[color:var(--teal)] to-transparent"
                  animate={{ x: ["-10%", "220%"] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
                />
              )}
            </div>

            {/* Chat area with faint watermark D */}
            <div className="relative flex-1 min-h-0">
              <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
                <span className="display" style={{ fontSize: "min(65vh, 460px)", lineHeight: 1, color: "var(--gold)", opacity: 0.02 }}>D</span>
              </div>
              <div ref={scrollRef} className="relative z-10 h-full overflow-y-auto p-6 space-y-5">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}-${i}`}>
                    {m.role === "user" ? (
                      <div className="max-w-[85%] p-4 clip-frame-sm text-sm leading-relaxed whitespace-pre-wrap" style={{ background: "var(--gold)", color: "#0A0E1A" }}>
                        {m.text}
                      </div>
                    ) : (
                      <AssistantBubble text={m.text} streaming={streaming && i === messages.length - 1} onRequestQuote={handleRequestQuote} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {messages.length <= 1 && (
              <div className="px-6 pb-3 relative z-10">
                <div className="font-mono-2 text-[9px] tracking-[0.3em] uppercase text-[color:var(--console-text)]/45 mb-3">Quick Start</div>
                <div className="grid grid-cols-2 gap-2">
                  {PROMPT_CARDS.map((c) => {
                    const Icon = c.icon;
                    return (
                      <button key={c.label} onClick={() => pickCard(c)}
                        data-testid={`ai-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="group flex items-center gap-2.5 px-3 py-3 border border-white/10 rounded-sm text-left hover:border-[color:var(--gold)]/50 hover:bg-white/[0.04] transition-colors duration-400">
                        <Icon size={15} className="text-[color:var(--teal)] group-hover:text-[color:var(--gold)] transition-colors duration-400 shrink-0" />
                        <span className="text-xs text-[color:var(--console-text)] leading-tight">{c.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); send(); }}
                  className="p-6 border-t border-white/10 flex gap-2 items-end relative z-10">
              <textarea
                ref={inputRef}
                rows={2} value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Describe your project or technical challenge…"
                aria-label="Message the Engineering Assistant"
                data-testid="ai-input"
                className="flex-1 resize-none bg-white/[0.05] border border-white/10 rounded-sm p-3 text-sm text-[color:var(--console-text)] placeholder:text-[color:var(--console-text)]/35 outline-none focus:border-[color:var(--gold)]/60 transition-colors duration-400"
              />
              <button type="submit" disabled={streaming || !input.trim()} data-testid="ai-send-btn" aria-label="Send message"
                      className="btn-gold rounded-sm w-12 h-12 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
